import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, CalendarBlank, ListBullets, CaretLeft, CaretRight,
  CheckCircle, XCircle, ArrowsClockwise, ClockCounterClockwise,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { userLabel } from "@/lib/helpers";

import { devLog } from "@/lib/devLog";
const KINDS = [
  { value: "administrative", label: "Administrative", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "special",        label: "Special",        color: "bg-purple-100 text-purple-800 border-purple-300" },
  { value: "onboarding",     label: "Onboarding",     color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
];
const kindMeta = (k) => KINDS.find(x => x.value === k) || KINDS[0];

const STATUS_STYLES = {
  scheduled: "bg-slate-100 text-slate-800",
  confirmed: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
  completed: "bg-emerald-100 text-emerald-800",
  no_show:   "bg-amber-100 text-amber-800",
};

const WRITE_ROLES = ["owner", "admin", "reception"];

function iso(date) { return dayjs(date).toISOString(); }
function toLocalInput(d) { return dayjs(d).format("YYYY-MM-DDTHH:mm"); }
function fromLocalInput(s) { return dayjs(s).toISOString(); }

const emptyForm = {
  title: "",
  kind: "administrative",
  start_at: toLocalInput(dayjs().add(1, "day").hour(10).minute(0)),
  end_at:   toLocalInput(dayjs().add(1, "day").hour(11).minute(0)),
  location: "",
  branch_id: "",
  attendee_user_ids: [],
  guardian_user_ids: [],
  student_id: "",
  notes: "",
  recur_enabled: false,
  recur_freq: "weekly",
  recur_mode: "count",  // 'count' | 'until'
  recur_count: 4,
  recur_until: dayjs().add(1, "month").format("YYYY-MM-DD"),
};

export default function AppointmentsPage() {
  const { user, activeRole } = useAuth();
  const canWrite = WRITE_ROLES.includes(activeRole) || (user?.roles || []).some(r => WRITE_ROLES.includes(r));

  const [view, setView] = useState("week");           // 'week' | 'list'
  const [weekStart, setWeekStart] = useState(dayjs().startOf("week"));
  const [appts, setAppts] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [students, setStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterKind, setFilterKind] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBranch, setFilterBranch] = useState("all");

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [availability, setAvailability] = useState({ available: true, conflicts: [], conflicts_by_attendee: {} });
  const [forceCreate, setForceCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [detailAppt, setDetailAppt] = useState(null);
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedForm, setReschedForm] = useState({ start_at: "", end_at: "", reason: "" });
  const [cancelReason, setCancelReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  // iter-67i (ID-36) — recurring-series deletion picker.
  const [deleteScope, setDeleteScope] = useState(null); // null | {aid, hasSeries}

  const guardians = useMemo(() => users.filter(u => (u.roles || []).includes("guardian")), [users]);
  const staff = useMemo(() => users.filter(u => !(u.roles || []).includes("guardian") && u.active !== false), [users]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (view === "week") {
        params.set("from", iso(weekStart.startOf("day")));
        params.set("to", iso(weekStart.add(7, "day").startOf("day")));
      } else {
        // list: past 30d → next 90d
        params.set("from", iso(dayjs().subtract(30, "day")));
        params.set("to", iso(dayjs().add(90, "day")));
      }
      if (filterKind !== "all") params.set("kind", filterKind);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterBranch !== "all") params.set("branch_id", filterBranch);
      const res = await api.get(`/appointments?${params.toString()}`);
      setAppts(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [view, weekStart, filterKind, filterStatus, filterBranch]);

  useEffect(() => {
    (async () => {
      try {
        const [u, e, s, b] = await Promise.all([
          api.get("/users"),
          // iter-67i (ID-32) — pull employees so attendee IDs that
          // don't resolve against the workspace users list (e.g. staff
          // added via Employee Master before /api/users backfill)
          // still render with a real name + email.
          api.get("/workforce/employees").catch(() => ({ data: [] })),
          api.get("/students").catch(() => ({ data: [] })),
          api.get("/branches").catch(() => ({ data: [] })),
        ]);
        setUsers(u.data || []);
        setEmployees(e.data || []);
        setStudents(s.data || []);
        setBranches(b.data || []);
      } catch (err) {
        devLog("appointments prefetch failed", err);
      }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---------- Availability check ----------
  useEffect(() => {
    if (!showCreate) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/appointments/check-availability", {
          attendee_user_ids: form.attendee_user_ids,
          guardian_user_ids: form.guardian_user_ids,
          start_at: fromLocalInput(form.start_at),
          end_at: fromLocalInput(form.end_at),
        });
        setAvailability(res.data);
      } catch (err) {
        setAvailability({ available: true, conflicts: [] });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [showCreate, form.attendee_user_ids, form.guardian_user_ids, form.start_at, form.end_at]);

  // ---------- Create ----------
  const openCreate = (defaults = {}) => {
    setForm({ ...emptyForm, ...defaults });
    setAvailability({ available: true, conflicts: [], conflicts_by_attendee: {} });
    setForceCreate(false);
    setShowCreate(true);
  };

  const submitCreate = async () => {
    if (!form.title.trim()) return toast.error("Please add a title");
    if (dayjs(form.end_at).valueOf() <= dayjs(form.start_at).valueOf()) return toast.error("End time must be after start time");
    // iter-67i (ID-37) — if any attendee-level conflict exists, require
    // the user to tick the "Create anyway" checkbox (drives `force`).
    const hasConflicts = Object.keys(availability.conflicts_by_attendee || {}).length > 0;
    if (hasConflicts && !forceCreate) {
      return toast.error("Attendee conflicts detected. Tick 'Create anyway' to proceed.");
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        kind: form.kind,
        start_at: fromLocalInput(form.start_at),
        end_at: fromLocalInput(form.end_at),
        branch_id: form.branch_id || null,
        location: form.location || null,
        attendee_user_ids: form.attendee_user_ids,
        guardian_user_ids: form.guardian_user_ids,
        student_id: form.kind === "onboarding" && form.student_id ? form.student_id : null,
        notes: form.notes || null,
      };
      if (form.recur_enabled) {
        payload.recurrence = { freq: form.recur_freq };
        if (form.recur_mode === "count") payload.recurrence.count = Number(form.recur_count);
        else payload.recurrence.until = form.recur_until;
      }
      const res = await api.post(`/appointments?force=${forceCreate ? "true" : "false"}`, payload);
      toast.success(res.data.count > 1 ? `Created ${res.data.count} appointments` : "Appointment created");
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Actions ----------
  const doAction = async (aid, action, body = null) => {
    try {
      const cfg = { method: "post", url: `/appointments/${aid}/${action}` };
      if (body) cfg.data = body;
      await api.request(cfg);
      toast.success(action.replace("-", " "));
      setDetailAppt(null);
      setShowCancel(false);
      setCancelReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitReschedule = async () => {
    if (!detailAppt) return;
    try {
      await api.put(`/appointments/${detailAppt.id}`, {
        start_at: fromLocalInput(reschedForm.start_at),
        end_at: fromLocalInput(reschedForm.end_at),
        reason: reschedForm.reason || null,
      });
      toast.success("Rescheduled");
      setReschedOpen(false);
      setDetailAppt(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteAppt = async (aid) => {
    // iter-67i (ID-36) — When the appointment belongs to a recurring
    // series, open a picker dialog so the caller chooses whether to
    // remove just this instance or every future instance in the
    // series. For one-off appointments the plain confirm is preserved.
    const appt = detailAppt && detailAppt.id === aid ? detailAppt : appts.find(a => a.id === aid);
    if (appt?.recurrence_group_id) {
      setDeleteScope({ aid, hasSeries: true });
      return;
    }
    if (!window.confirm("Delete this appointment permanently? Only 'scheduled' appointments can be deleted; otherwise cancel instead.")) return;
    try {
      await api.delete(`/appointments/${aid}?scope=this`);
      toast.success("Deleted");
      setDetailAppt(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const performDeleteScope = async (scope) => {
    if (!deleteScope) return;
    try {
      const res = await api.delete(`/appointments/${deleteScope.aid}?scope=${scope}`);
      const n = res?.data?.deleted_count ?? 1;
      toast.success(`Deleted ${n} appointment${n === 1 ? "" : "s"}`);
      setDeleteScope(null);
      setDetailAppt(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---------- Week grid data ----------
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day")), [weekStart]);
  const byDay = useMemo(() => {
    const map = {};
    days.forEach(d => { map[d.format("YYYY-MM-DD")] = []; });
    appts.forEach(a => {
      const key = dayjs(a.start_at).format("YYYY-MM-DD");
      if (map[key]) map[key].push(a);
    });
    Object.values(map).forEach(list => list.sort((x, y) => x.start_at.localeCompare(y.start_at)));
    return map;
  }, [appts, days]);

  const userNameById = (id) => {
    // iter-67i (ID-32) — never show raw IDs. Try users, then
    // employees (linked user_id), then fall back to a generic label.
    if (!id) return "Unknown attendee";
    const u = users.find(x => x.id === id);
    if (u) return userLabel(u);
    const emp = employees.find(e => e.user_id === id || e.id === id);
    if (emp) {
      const name = emp.name || "";
      const email = emp.email || "";
      return name ? (email ? `${name} (${email})` : name) : (email || "Unknown attendee");
    }
    return "Unknown attendee";
  };

  // ---------- Renders ----------
  const renderAppointmentChip = (a) => {
    const meta = kindMeta(a.kind);
    return (
      <button
        key={a.id}
        data-testid={`appt-chip-${a.id}`}
        onClick={() => setDetailAppt(a)}
        className={`w-full text-left rounded-md border px-2 py-1.5 mb-1.5 text-xs hover:shadow-sm transition ${meta.color} ${a.status === "cancelled" ? "opacity-50 line-through" : ""}`}
      >
        <div className="font-medium truncate">{a.title}</div>
        <div className="opacity-80">{dayjs(a.start_at).format("HH:mm")} – {dayjs(a.end_at).format("HH:mm")}</div>
        {a.status !== "scheduled" && (
          <Badge variant="outline" className={`mt-1 text-[10px] ${STATUS_STYLES[a.status] || ""}`}>{a.status.replace("_", " ")}</Badge>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Appointments</h1>
          <p className="text-sm text-muted-foreground">
            Administrative meetings, special reviews, and new-student onboarding.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border overflow-hidden">
            <Button
              data-testid="view-week-btn"
              variant={view === "week" ? "default" : "ghost"}
              size="sm"
              className="rounded-none"
              onClick={() => setView("week")}
            >
              <CalendarBlank size={16} className="mr-1" /> Week
            </Button>
            <Button
              data-testid="view-list-btn"
              variant={view === "list" ? "default" : "ghost"}
              size="sm"
              className="rounded-none"
              onClick={() => setView("list")}
            >
              <ListBullets size={16} className="mr-1" /> List
            </Button>
          </div>
          {canWrite && (
            <Button data-testid="new-appointment-btn" onClick={() => openCreate()}>
              <Plus size={16} className="mr-1" /> New appointment
            </Button>
          )}
        </div>
      </div>

      {/* Filters + Week nav */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-wrap items-center gap-3">
            {view === "week" && (
              <div className="flex items-center gap-2">
                <Button data-testid="week-prev-btn" variant="outline" size="icon" onClick={() => setWeekStart(w => w.subtract(7, "day"))}>
                  <CaretLeft size={16} />
                </Button>
                <div className="text-sm font-medium min-w-[220px] text-center" data-testid="week-range-label">
                  {weekStart.format("MMM D")} – {weekStart.add(6, "day").format("MMM D, YYYY")}
                </div>
                <Button data-testid="week-next-btn" variant="outline" size="icon" onClick={() => setWeekStart(w => w.add(7, "day"))}>
                  <CaretRight size={16} />
                </Button>
                <Button data-testid="week-today-btn" variant="ghost" size="sm" onClick={() => setWeekStart(dayjs().startOf("week"))}>
                  Today
                </Button>
              </div>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Select value={filterKind} onValueChange={setFilterKind}>
                <SelectTrigger className="w-[160px]" data-testid="filter-kind"><SelectValue placeholder="Kind" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All kinds</SelectItem>
                  {KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {["scheduled","confirmed","cancelled","completed","no_show"].map(s =>
                    <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {branches.length > 0 && (
                <Select value={filterBranch} onValueChange={setFilterBranch}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Week grid */}
      {view === "week" && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="week-grid">
          {days.map(d => {
            const key = d.format("YYYY-MM-DD");
            const isToday = d.isSame(dayjs(), "day");
            return (
              <Card key={key} className={isToday ? "border-primary" : ""}>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>{d.format("ddd")}</span>
                    <span className={`text-lg font-semibold ${isToday ? "text-primary" : ""}`}>{d.format("D")}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 min-h-[160px]">
                  {(byDay[key] || []).map(renderAppointmentChip)}
                  {canWrite && (
                    <button
                      onClick={() => openCreate({
                        start_at: toLocalInput(d.hour(10).minute(0)),
                        end_at: toLocalInput(d.hour(11).minute(0)),
                      })}
                      className="w-full text-xs text-muted-foreground border border-dashed rounded-md py-1.5 hover:bg-accent"
                      data-testid={`quick-add-${key}`}
                    >
                      + Add
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* List view */}
      {view === "list" && (
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading…</div>
            ) : appts.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground" data-testid="empty-state">
                No appointments in the selected range.
              </div>
            ) : (
              <div className="divide-y" data-testid="appt-list">
                {appts.map(a => {
                  const meta = kindMeta(a.kind);
                  return (
                    <button
                      key={a.id}
                      data-testid={`appt-row-${a.id}`}
                      onClick={() => setDetailAppt(a)}
                      className={`w-full text-left p-4 hover:bg-accent flex items-start gap-4 ${a.status === "cancelled" ? "opacity-60" : ""}`}
                    >
                      <div className="w-14 shrink-0 text-center">
                        <div className="text-xs text-muted-foreground">{dayjs(a.start_at).format("ddd")}</div>
                        <div className="text-lg font-semibold">{dayjs(a.start_at).format("D")}</div>
                        <div className="text-xs text-muted-foreground">{dayjs(a.start_at).format("MMM")}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{a.title}</span>
                          <Badge variant="outline" className={meta.color}>{meta.label}</Badge>
                          <Badge variant="outline" className={STATUS_STYLES[a.status] || ""}>{a.status.replace("_"," ")}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {dayjs(a.start_at).format("HH:mm")} – {dayjs(a.end_at).format("HH:mm")}
                          {a.location ? ` · ${a.location}` : ""}
                        </div>
                        {(a.attendee_user_ids?.length || 0) + (a.guardian_user_ids?.length || 0) > 0 && (
                          <div className="text-xs text-muted-foreground mt-1 truncate">
                            {[...(a.attendee_user_ids || []), ...(a.guardian_user_ids || [])].map(userNameById).join(", ")}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CREATE dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl" data-testid="create-appointment-dialog">
          <DialogHeader>
            <DialogTitle>New appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label>Title *</Label>
              <Input
                data-testid="title-input"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Q3 planning meeting"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kind *</Label>
                <Select value={form.kind} onValueChange={v => setForm(f => ({ ...f, kind: v }))}>
                  <SelectTrigger data-testid="kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {branches.length > 0 && (
                <div>
                  <Label>Branch</Label>
                  <Select value={form.branch_id || "none"} onValueChange={v => setForm(f => ({ ...f, branch_id: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="branch-select"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start *</Label>
                <Input
                  type="datetime-local"
                  data-testid="start-input"
                  value={form.start_at}
                  onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))}
                />
              </div>
              <div>
                <Label>End *</Label>
                <Input
                  type="datetime-local"
                  data-testid="end-input"
                  value={form.end_at}
                  onChange={e => setForm(f => ({ ...f, end_at: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                data-testid="location-input"
                value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="Room name, Zoom link, address…"
              />
            </div>

            {/* Attendees */}
            <div>
              <Label>Staff attendees</Label>
              <MultiUserPicker
                users={staff}
                selected={form.attendee_user_ids}
                onChange={ids => setForm(f => ({ ...f, attendee_user_ids: ids }))}
                testid="staff-picker"
              />
            </div>

            {form.kind === "onboarding" && (
              <>
                <div>
                  <Label>Guardian attendees</Label>
                  <MultiUserPicker
                    users={guardians}
                    selected={form.guardian_user_ids}
                    onChange={ids => setForm(f => ({ ...f, guardian_user_ids: ids }))}
                    testid="guardian-picker"
                  />
                </div>
                <div>
                  <Label>New student (optional)</Label>
                  <Select
                    value={form.student_id || "none"}
                    onValueChange={v => setForm(f => ({ ...f, student_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger data-testid="student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Recurrence */}
            <div className="border-t pt-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="recur-toggle"
                  checked={form.recur_enabled}
                  onChange={e => setForm(f => ({ ...f, recur_enabled: e.target.checked }))}
                />
                <ArrowsClockwise size={14} /> Repeat this appointment
              </label>
              {form.recur_enabled && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label>Frequency</Label>
                    <Select value={form.recur_freq} onValueChange={v => setForm(f => ({ ...f, recur_freq: v }))}>
                      <SelectTrigger data-testid="recur-freq"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Ends</Label>
                    <Select value={form.recur_mode} onValueChange={v => setForm(f => ({ ...f, recur_mode: v }))}>
                      <SelectTrigger data-testid="recur-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="count">After N occurrences</SelectItem>
                        <SelectItem value="until">On date</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.recur_mode === "count" ? (
                    <div>
                      <Label>Occurrences (max 52)</Label>
                      <Input
                        type="number" min={2} max={52}
                        data-testid="recur-count"
                        value={form.recur_count}
                        onChange={e => setForm(f => ({ ...f, recur_count: e.target.value }))}
                      />
                    </div>
                  ) : (
                    <div>
                      <Label>Until (inclusive)</Label>
                      <Input
                        type="date"
                        data-testid="recur-until"
                        value={form.recur_until}
                        onChange={e => setForm(f => ({ ...f, recur_until: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                data-testid="notes-input"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Agenda, prep, etc."
              />
            </div>

            {/* Availability hint */}
            {!availability.available && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 space-y-2" data-testid="conflict-warning">
                <div className="font-medium">Attendee conflicts detected</div>
                {Object.entries(availability.conflicts_by_attendee || {}).length > 0 ? (
                  <ul className="list-disc ml-5 space-y-1">
                    {Object.entries(availability.conflicts_by_attendee).map(([aid, list]) => (
                      <li key={aid}>
                        <b>{userNameById(aid)}</b> — {list.length} overlapping appointment{list.length > 1 ? "s" : ""}:
                        <ul className="list-[circle] ml-5">
                          {list.map(c => (
                            <li key={c.id}>{c.title} · {dayjs(c.start_at).format("MMM D, HH:mm")}</li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="list-disc ml-5 mt-1">
                    {availability.conflicts.map(c => (
                      <li key={c.id}>{c.title} — {dayjs(c.start_at).format("MMM D, HH:mm")}</li>
                    ))}
                  </ul>
                )}
                <label className="flex items-center gap-2 pt-1 border-t border-amber-300 mt-2 text-amber-900 font-medium">
                  <input
                    type="checkbox"
                    data-testid="force-create-toggle"
                    checked={forceCreate}
                    onChange={e => setForceCreate(e.target.checked)}
                  />
                  Create anyway (I've confirmed with the affected attendees)
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="submit-create" onClick={submitCreate} disabled={saving}>
              {saving ? "Saving…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL dialog */}
      <Dialog open={!!detailAppt} onOpenChange={(o) => !o && setDetailAppt(null)}>
        <DialogContent className="max-w-lg" data-testid="detail-dialog">
          {detailAppt && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span>{detailAppt.title}</span>
                  <Badge variant="outline" className={kindMeta(detailAppt.kind).color}>{kindMeta(detailAppt.kind).label}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <Row label="When">
                  {dayjs(detailAppt.start_at).format("ddd MMM D, YYYY · HH:mm")} – {dayjs(detailAppt.end_at).format("HH:mm")}
                </Row>
                <Row label="Status">
                  <Badge variant="outline" className={STATUS_STYLES[detailAppt.status] || ""}>{detailAppt.status.replace("_"," ")}</Badge>
                </Row>
                {detailAppt.location && <Row label="Location">{detailAppt.location}</Row>}
                {detailAppt.branch_id && (
                  <Row label="Branch">{branches.find(b => b.id === detailAppt.branch_id)?.name || "—"}</Row>
                )}
                {(detailAppt.attendee_user_ids?.length || 0) > 0 && (
                  <Row label="Staff">{detailAppt.attendee_user_ids.map(userNameById).join(", ")}</Row>
                )}
                {(detailAppt.guardian_user_ids?.length || 0) > 0 && (
                  <Row label="Guardians">{detailAppt.guardian_user_ids.map(userNameById).join(", ")}</Row>
                )}
                {detailAppt.student_id && (
                  <Row label="Student">{students.find(s => s.id === detailAppt.student_id)?.name || "—"}</Row>
                )}
                {detailAppt.notes && <Row label="Notes"><span className="whitespace-pre-wrap">{detailAppt.notes}</span></Row>}
                {detailAppt.recurrence_group_id && (
                  <div className="text-xs text-muted-foreground">Part of a recurring series</div>
                )}
              </div>
              {canWrite && (
                <DialogFooter className="flex-wrap gap-2 items-center">
                  {detailAppt.status === "scheduled" && (
                    <Button data-testid="action-confirm" size="sm" variant="outline" onClick={() => doAction(detailAppt.id, "confirm")}>
                      <CheckCircle size={14} className="mr-1" /> Confirm
                    </Button>
                  )}
                  {["scheduled", "confirmed"].includes(detailAppt.status) && (
                    <>
                      <Button data-testid="action-reschedule" size="sm" variant="outline" onClick={() => {
                        setReschedForm({
                          start_at: toLocalInput(detailAppt.start_at),
                          end_at: toLocalInput(detailAppt.end_at),
                          reason: "",
                        });
                        setReschedOpen(true);
                      }}>
                        <ClockCounterClockwise size={14} className="mr-1" /> Reschedule
                      </Button>
                      <Button data-testid="action-cancel" size="sm" variant="outline" onClick={() => setShowCancel(true)}>
                        <XCircle size={14} className="mr-1" /> Cancel
                      </Button>
                      <Button data-testid="action-complete" size="sm" variant="outline" onClick={() => doAction(detailAppt.id, "complete", { reason: null })}>
                        <CheckCircle size={14} className="mr-1" /> Complete
                      </Button>
                      <Button data-testid="action-no-show" size="sm" variant="outline" onClick={() => doAction(detailAppt.id, "no-show")}>
                        No-show
                      </Button>
                    </>
                  )}
                  {detailAppt.status === "scheduled" && (
                    <Button data-testid="action-delete" size="sm" variant="ghost" className="text-red-600" onClick={() => deleteAppt(detailAppt.id)}>
                      Delete
                    </Button>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={reschedOpen} onOpenChange={setReschedOpen}>
        <DialogContent data-testid="resched-dialog">
          <DialogHeader><DialogTitle>Reschedule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New start</Label>
              <Input type="datetime-local" data-testid="resched-start"
                value={reschedForm.start_at}
                onChange={e => setReschedForm(f => ({ ...f, start_at: e.target.value }))} />
            </div>
            <div>
              <Label>New end</Label>
              <Input type="datetime-local" data-testid="resched-end"
                value={reschedForm.end_at}
                onChange={e => setReschedForm(f => ({ ...f, end_at: e.target.value }))} />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input data-testid="resched-reason"
                value={reschedForm.reason}
                onChange={e => setReschedForm(f => ({ ...f, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedOpen(false)}>Close</Button>
            <Button data-testid="resched-submit" onClick={submitReschedule}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader><DialogTitle>Cancel appointment</DialogTitle></DialogHeader>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea
              data-testid="cancel-reason"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Why is this being cancelled?"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>Back</Button>
            <Button data-testid="cancel-submit" variant="destructive"
              onClick={() => doAction(detailAppt.id, "cancel", { reason: cancelReason || null })}>
              Cancel appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter-67i (ID-36) — Series delete picker. Opens only when the
          selected appointment belongs to a recurring group. */}
      <Dialog open={!!deleteScope} onOpenChange={(o) => !o && setDeleteScope(null)}>
        <DialogContent className="max-w-md" data-testid="delete-scope-dialog">
          <DialogHeader>
            <DialogTitle>Delete recurring appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>This appointment is part of a recurring series. Choose what to delete:</p>
            <div className="grid gap-2">
              <button
                data-testid="delete-scope-this"
                onClick={() => performDeleteScope("this")}
                className="text-left rounded-md border p-3 hover:bg-muted"
              >
                <div className="font-medium">Delete this appointment only</div>
                <div className="text-xs text-muted-foreground">Removes just the selected instance. Every other appointment in the series stays.</div>
              </button>
              <button
                data-testid="delete-scope-future"
                onClick={() => performDeleteScope("future")}
                className="text-left rounded-md border border-rose-300 p-3 hover:bg-rose-50"
              >
                <div className="font-medium text-rose-800">Delete this and all future appointments</div>
                <div className="text-xs text-rose-700/80">Removes the selected instance plus every later instance in the series. Past instances are not affected.</div>
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteScope(null)} data-testid="delete-scope-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-start">
      <div className="text-xs uppercase tracking-wide text-muted-foreground pt-0.5">{label}</div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}

function MultiUserPicker({ users, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = users.filter(u => {
    const label = (userLabel(u) || "").toLowerCase();
    return label.includes(q.toLowerCase());
  });
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1 mb-2">
        {selected.length === 0 && <span className="text-xs text-muted-foreground">No one selected</span>}
        {selected.map(id => {
          const u = users.find(x => x.id === id);
          return (
            <Badge key={id} variant="secondary" className="cursor-pointer" onClick={() => toggle(id)}>
              {u ? userLabel(u) : id.slice(0, 6)} ×
            </Badge>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input
          data-testid={`${testid}-search`}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search…"
        />
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(o => !o)}>
          {open ? "Hide" : "Pick"}
        </Button>
      </div>
      {open && (
        <div className="mt-2 border rounded-md max-h-40 overflow-y-auto text-sm">
          {filtered.length === 0 && <div className="p-2 text-muted-foreground text-xs">No matches</div>}
          {filtered.slice(0, 50).map(u => (
            <div
              key={u.id}
              onClick={() => toggle(u.id)}
              className={`px-3 py-1.5 hover:bg-accent cursor-pointer flex items-center justify-between ${selected.includes(u.id) ? "bg-accent/60" : ""}`}
              data-testid={`${testid}-item-${u.id}`}
            >
              <span>{userLabel(u)}</span>
              {selected.includes(u.id) && <span className="text-xs text-primary">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
